import { useState, type FormEvent } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { useFamilies, type FamilySummary } from '@wovendays/api-client';
import { useTranslation } from '@wovendays/i18n';
import { apiClient } from '@/lib/api-client';
import { Tile } from '@/components/ui/tile';
import { Input } from '@/components/ui/input';
import { Button } from '@/components/ui/button';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { Badge } from '@/components/ui/badge';

const avatarColors = ['bg-primary-500', 'bg-amber-500', 'bg-emerald-500', 'bg-violet-500', 'bg-rose-500', 'bg-cyan-500'];

export function FamiliesPage() {
  const { data, isLoading } = useFamilies();
  const { t } = useTranslation('common');

  const families = data?.families ?? [];

  if (isLoading) {
    return (
      <div data-testid="families-loading" className="flex items-center justify-center py-12" role="status" aria-label={t('loading')}>
        <div className="inline-block h-6 w-6 animate-spin rounded-full border-4 border-muted border-t-primary-600" />
      </div>
    );
  }

  if (families.length === 0) {
    return <CreateFamilyOnboarding />;
  }

  return (
    <div>
      <Tile variant="white" className="p-5 mb-3 flex items-center justify-between">
        <h2 className="font-serif text-xl text-foreground">{t('timeline:families')}</h2>
        <CreateFamilyButton />
      </Tile>
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
        {families.map((family) => (
          <FamilyCard key={family.id} family={family} />
        ))}
      </div>
    </div>
  );
}

function FamilyCard({ family }: { family: FamilySummary }) {
  const { t } = useTranslation('common');
  return (
    <Link to={`/families/${family.id}/timeline`} className="block">
      <Tile variant="cream" interactive className="p-5 h-full">
        {/* Header: name + role badge */}
        <div className="flex items-start justify-between gap-2 mb-3">
          <h3 className="font-serif font-bold text-card-foreground leading-tight">{family.name}</h3>
          <Badge variant="secondary" className="shrink-0 text-[10px]">{family.role}</Badge>
        </div>

        {/* Recent photos */}
        {family.recentPhotos.length > 0 && (
          <div className="grid grid-cols-3 gap-1.5 mb-3">
            {family.recentPhotos.map((photo) => (
              <div key={photo.id} className="aspect-square rounded-lg overflow-hidden bg-muted">
                <img
                  src={photo.gridUrl ?? ''}
                  alt=""
                  className="h-full w-full object-cover"
                  loading="lazy"
                />
              </div>
            ))}
          </div>
        )}

        {/* Members */}
        <div className="mb-2">
          <p className="font-mono text-[10px] uppercase tracking-wide text-muted-foreground mb-1">
            {t('members', { count: family.memberCount })}
          </p>
          <div className="flex flex-wrap gap-1">
            {family.members.map((member, i) => (
              <span key={i} className="inline-flex items-center gap-1 rounded-full bg-background px-2 py-0.5 text-xs text-foreground">
                <span className={`inline-flex h-4 w-4 items-center justify-center rounded-full text-white text-[8px] font-semibold ${avatarColors[i % avatarColors.length]}`}>
                  {member.name.charAt(0).toUpperCase()}
                </span>
                {member.name}
              </span>
            ))}
            {family.memberCount > 5 && (
              <span className="inline-flex items-center rounded-full bg-muted px-2 py-0.5 text-xs text-muted-foreground">
                +{family.memberCount - 5}
              </span>
            )}
          </div>
        </div>

        {/* Children */}
        {family.childrenCount > 0 && (
          <div>
            <p className="font-mono text-[10px] uppercase tracking-wide text-muted-foreground mb-1">
              {t('children', { count: family.childrenCount })}
            </p>
            <div className="flex flex-wrap gap-1">
              {family.children.map((child) => (
                <span key={child.id} className="inline-flex items-center rounded-full bg-background px-2 py-0.5 text-xs text-foreground">
                  {child.name}
                </span>
              ))}
            </div>
          </div>
        )}
      </Tile>
    </Link>
  );
}

function CreateFamilyOnboarding() {
  const navigate = useNavigate();
  const { t } = useTranslation('onboarding');

  return (
    <div>
      <Tile variant="dark" className="p-8 text-center">
        <h2 className="font-serif text-2xl text-white mb-3">{t('common:appName')}</h2>
        <p className="text-zinc-400 mb-6">
          {t('createFamily.description')}
        </p>
        <Button onClick={() => navigate('/onboarding')} className="mb-6">
          {t('common:continue')}
        </Button>
        <div className="mx-auto max-w-sm">
          <p className="text-zinc-500 text-xs mb-3">{t('common:or')}:</p>
          <CreateFamilyForm />
        </div>
      </Tile>
    </div>
  );
}

function CreateFamilyButton() {
  const [showForm, setShowForm] = useState(false);
  const { t } = useTranslation('onboarding');

  if (!showForm) {
    return (
      <Button onClick={() => setShowForm(true)} size="sm">
        {t('createFamily.createFamily')}
      </Button>
    );
  }

  return <CreateFamilyForm onCancel={() => setShowForm(false)} />;
}

function CreateFamilyForm({ onCancel }: { onCancel?: () => void }) {
  const { t } = useTranslation('onboarding');
  const [name, setName] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [validationError, setValidationError] = useState<string | null>(null);
  const queryClient = useQueryClient();

  const mutation = useMutation({
    mutationFn: (familyName: string) =>
      apiClient.post<{ family: FamilySummary }>('/api/families', { name: familyName }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['families'] });
      setName('');
      onCancel?.();
    },
    onError: (err) => {
      setError(err instanceof Error ? err.message : t('createFamily.failed'));
    },
  });

  function handleSubmit(e: FormEvent) {
    e.preventDefault();
    setError(null);
    setValidationError(null);

    if (!name.trim()) {
      setValidationError(t('createFamily.nameRequired'));
      return;
    }

    mutation.mutate(name.trim());
  }

  return (
    <form onSubmit={handleSubmit} className="w-full max-w-sm">
      {error && (
        <Alert variant="destructive" className="mb-3">
          <AlertDescription>{error}</AlertDescription>
        </Alert>
      )}
      <div className="mb-3">
        <label htmlFor="family-name" className="block font-mono text-xs uppercase tracking-wide text-muted-foreground mb-1.5">
          {t('createFamily.nameLabel')}
        </label>
        <Input
          id="family-name"
          type="text"
          value={name}
          onChange={(e) => setName(e.target.value)}
          placeholder={t('createFamily.namePlaceholder')}
        />
        {validationError && (
          <p className="mt-1.5 text-sm text-destructive">{validationError}</p>
        )}
      </div>
      <div className="flex gap-2">
        <Button type="submit" disabled={mutation.isPending}>
          {mutation.isPending ? t('createFamily.creating') : t('createFamily.createFamily')}
        </Button>
        {onCancel && (
          <Button type="button" variant="ghost" onClick={onCancel}>
            {t('common:cancel')}
          </Button>
        )}
      </div>
    </form>
  );
}
